import React from 'react';
import Error from '../Error/Error';
import styles from './FormField.module.scss';

type OptionsType = string[] | { [key: string]: string };

type FormFieldProps = {
    label: string;
    type: string;
    name: string;
    formik: any;
    options?: OptionsType;
};

export default function FormField({ label, type, name, formik, options }: FormFieldProps) {
    const renderSelect = () => {
        if (Array.isArray(options)) {
            return options.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ));
        } else if (typeof options === 'object' && options !== null) {
            return Object.entries(options).map(([key, value]) => (
                <option key={key} value={key}>
                    {value}
                </option>
            ));
        }
    };

    const renderRadio = () => {
        const optionElements = Array.isArray(options)
            ? options.map((option) => ({
                key: option,
                value: option,
                label: option,
            }))
            : Object.entries(options).map(([key, value]) => ({
                key: key,
                value: key,
                label: value,
            }));

        return (
            <div className={styles.radios}>
                {optionElements.map(({ key, value, label }) => (
                    <label className="radio" key={key}>
                        <input
                            type="radio"
                            name={name}
                            value={value}
                            checked={formik.values[name] === value}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        <span>{label}</span>
                    </label>
                ))}
            </div>
        );
    };

    const renderCheckbox = () => {
        // Ensure formik.values[name] is an array for multiple checkboxes
        const currentValues = Array.isArray(formik.values[name]) ? formik.values[name] : [];

        const optionElements = Array.isArray(options)
            ? options.map((option) => ({
                key: option,
                value: option,
                label: option,
            }))
            : Object.entries(options).map(([key, value]) => ({
                key: key,
                value: key,
                label: value,
            }));

        return (
            <div className={styles.checkboxes}>
                {optionElements.map(({ key, value, label }) => (
                    <label className="checkbox" key={key}>
                        <input
                            type="checkbox"
                            name={name}
                            value={value}
                            checked={currentValues.includes(value)}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    formik.setFieldValue(name, [...currentValues, value]);
                                } else {
                                    formik.setFieldValue(name, currentValues.filter((item) => item !== value));
                                }
                            }}
                            onBlur={formik.handleBlur}
                        />
                        <span>{label}</span>
                    </label>
                ))}
            </div>
        );
    };

    const renderInput = () => {
        switch (type) {
            case 'textarea':
                return (
                    <textarea
                        name={name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[name]}
                    />
                );
            case 'select':
                return (
                    <select
                        name={name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[name]}
                    >
                        {renderSelect()}
                    </select>
                );
            case 'radio':
                return renderRadio();
            case 'checkbox':
                return renderCheckbox();
            default:
                return (
                    <input
                        type={type}
                        name={name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[name]}
                    />
                );
        }
    };

    return (
        <div className="meatex-form-row">
            <label>
                {label}
                {renderInput()}
            </label>
            <Error touched={formik.touched[name]} error={formik.errors[name]} />
        </div>
    );
}